import React, { useState, useEffect, useCallback, useRef, FormEvent } from 'react';
import { apiGet, apiPatch, apiUpload, apiDownload, apiDelete } from '../api/client';
import type { DocumentListResponse, DocumentResponse } from '../types/api';
import { DocumentCategory, VaultRole } from '../types/api';
import SortableList from './SortableList';
import AlertMessage from './AlertMessage';
import LoadingSpinner from './LoadingSpinner';
import Skeleton from './Skeleton';
import EmptyState from './EmptyState';
import DropZone from './DropZone';
import './DocumentsList.css';

interface Props {
  vaultId: string;
  userRole: VaultRole;
  onCountChange?: (count: number) => void;
}

const categoryLabels: Record<number, string> = {
  [DocumentCategory.Will]: 'Last Will & Testament',
  [DocumentCategory.Trust]: 'Trust',
  [DocumentCategory.Insurance]: 'Insurance',
  [DocumentCategory.Medical]: 'Medical',
  [DocumentCategory.Financial]: 'Financial',
  [DocumentCategory.Photo]: 'Photo',
  [DocumentCategory.Video]: 'Video',
  [DocumentCategory.Other]: 'Other',
  [DocumentCategory.Deed]: 'Deed',
  [DocumentCategory.PowerOfAttorney]: 'Power of Attorney',
  [DocumentCategory.BirthCertificate]: 'Birth Certificate',
  [DocumentCategory.MarriageCertificate]: 'Marriage Certificate',
  [DocumentCategory.DeathCertificate]: 'Death Certificate',
  [DocumentCategory.SocialSecurity]: 'Social Security',
  [DocumentCategory.TaxRecord]: 'Tax Record',
  [DocumentCategory.FuneralInstructions]: 'Funeral Instructions',
  [DocumentCategory.PersonalLetter]: 'Personal Letter',
  [DocumentCategory.Membership]: 'Membership',
};

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function DocumentsList({ vaultId, userRole, onCountChange }: Props) {
  const onCountChangeRef = useRef(onCountChange);
  onCountChangeRef.current = onCountChange;

  const [documents, setDocuments] = useState<DocumentResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [category, setCategory] = useState<DocumentCategory>(DocumentCategory.Other);
  const [droppedFile, setDroppedFile] = useState<File | null>(null);
  const [thumbUrls, setThumbUrls] = useState<Record<string, string>>({});
  const [confirmDelete, setConfirmDelete] = useState<DocumentResponse | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [previewDoc, setPreviewDoc] = useState<DocumentResponse | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const canDelete = userRole === VaultRole.Owner || userRole === VaultRole.Spouse;
  const canReorder = userRole === VaultRole.Owner || userRole === VaultRole.Spouse;

  const loadDocuments = useCallback(async () => {
    try {
      const data = await apiGet<DocumentListResponse>(`/api/vault/${vaultId}/documents`);
      setDocuments(data.documents);
      onCountChangeRef.current?.(data.documents.length);
    } catch (err: any) {
      setError(err.message || 'Failed to load documents.');
    } finally {
      setLoading(false);
    }
  }, [vaultId]);

  async function handleReorder(reordered: DocumentResponse[]) {
    setDocuments(reordered);
    try {
      await apiPatch(`/api/vault/${vaultId}/documents/reorder`, {
        orderedIds: reordered.map(d => d.id),
      });
    } catch {
      await loadDocuments();
    }
  }

  useEffect(() => { loadDocuments(); }, [loadDocuments]);

  // Load thumbnails for image documents
  useEffect(() => {
    documents
      .filter(d => d.contentType.startsWith('image/') && !thumbUrls[d.id])
      .forEach(async (d) => {
        try {
          const { blob } = await apiDownload(
            `/api/vault/${vaultId}/documents/${d.id}/download`
          );
          const url = URL.createObjectURL(blob);
          setThumbUrls(prev => ({ ...prev, [d.id]: url }));
        } catch {
          // Thumbnail load failed silently
        }
      });
  }, [documents, vaultId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleDelete() {
    if (!confirmDelete) return;
    setDeleting(true);
    setError('');
    try {
      await apiDelete(`/api/vault/${vaultId}/documents/${confirmDelete.id}`);
      // Revoke thumbnail URL if it existed
      if (thumbUrls[confirmDelete.id]) {
        URL.revokeObjectURL(thumbUrls[confirmDelete.id]);
        setThumbUrls(prev => {
          const next = { ...prev };
          delete next[confirmDelete.id];
          return next;
        });
      }
      setConfirmDelete(null);
      await loadDocuments();
    } catch (err: any) {
      setError(err.message || 'Delete failed.');
      setConfirmDelete(null);
    } finally {
      setDeleting(false);
    }
  }

  async function handleUpload(e: FormEvent) {
    e.preventDefault();
    setError('');

    const file = droppedFile || fileRef.current?.files?.[0];
    if (!file) {
      setError('Please select a file.');
      return;
    }

    if (file.type.startsWith('video/')) {
      setError('Video files belong in Legacy Messages. Navigate to the Legacy Messages tab to upload a video for your family.');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', String(category));
      await apiUpload(`/api/vault/${vaultId}/documents`, formData);
      setShowUpload(false);
      setDroppedFile(null);
      setCategory(DocumentCategory.Other);
      if (fileRef.current) fileRef.current.value = '';
      await loadDocuments();
    } catch (err: any) {
      setError(err.message || 'Upload failed.');
    } finally {
      setUploading(false);
    }
  }

  function handleFileDrop(files: FileList) {
    const file = files[0];
    if (file?.type.startsWith('video/')) {
      setError('Video files belong in Legacy Messages. Navigate to the Legacy Messages tab to upload a video for your family.');
      return;
    }
    setDroppedFile(file);
    setShowUpload(true);
  }

  async function handleDownload(doc: DocumentResponse) {
    try {
      const { blob, fileName } = await apiDownload(
        `/api/vault/${vaultId}/documents/${doc.id}/download`
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.message || 'Download failed.');
    }
  }

  function isPreviewable(contentType: string): boolean {
    return contentType.startsWith('image/') || contentType === 'application/pdf';
  }

  async function handlePreview(doc: DocumentResponse) {
    if (!isPreviewable(doc.contentType)) return;
    setPreviewLoading(doc.id);
    try {
      const { blob } = await apiDownload(
        `/api/vault/${vaultId}/documents/${doc.id}/download`
      );
      const url = URL.createObjectURL(blob);
      setPreviewDoc(doc);
      setPreviewUrl(url);
    } catch (err: any) {
      setError(err.message || 'Failed to load preview.');
    } finally {
      setPreviewLoading(null);
    }
  }

  function closePreview() {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewDoc(null);
    setPreviewUrl(null);
  }

  // Close preview on Escape
  useEffect(() => {
    if (!previewDoc) return;
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') closePreview(); }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewDoc]);

  if (loading) return <Skeleton variant="row" count={4} />;

  return (
    <div>
      <div className="docs-header">
        <h3>Documents</h3>
        <button
          className="btn-text"
          onClick={() => canDelete && setShowUpload(!showUpload)}
          disabled={!canDelete}
        >
          {showUpload ? 'Cancel' : '+ Upload'}
        </button>
      </div>

      <AlertMessage type="error" message={error} />

      {showUpload && (
        <form className="doc-upload-form" onSubmit={handleUpload}>
          <div className="doc-upload-row">
            <div className="form-group">
              <label className="form-label" htmlFor="docFile">File</label>
              {droppedFile ? (
                <div className="doc-dropped-file">
                  <span className="doc-dropped-name">{droppedFile.name}</span>
                  <button
                    type="button"
                    className="btn-text doc-dropped-clear"
                    onClick={() => setDroppedFile(null)}
                  >
                    Change
                  </button>
                </div>
              ) : (
                <input
                  id="docFile"
                  className="form-input doc-upload-input"
                  type="file"
                  ref={fileRef}
                  required
                />
              )}
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="docCategory">Category</label>
              <select
                id="docCategory"
                className="form-input"
                value={category}
                onChange={e => setCategory(Number(e.target.value))}
              >
                {Object.entries(categoryLabels).map(([val, label]) => (
                  <option key={val} value={val}>{label}</option>
                ))}
              </select>
            </div>
            <button className="btn-primary" type="submit" disabled={uploading}>
              {uploading ? <LoadingSpinner small /> : 'Upload'}
            </button>
          </div>
        </form>
      )}

      <DropZone onFileDrop={handleFileDrop} label="Drop file here" enabled={canDelete}>
        {documents.length === 0 ? (
          <EmptyState
            icon="documents"
            title="No documents yet"
            subtitle="Drag a file here or click '+ Upload' above."
          />
        ) : (
          <div className="docs-list">
            <SortableList
              items={documents}
              canReorder={canReorder}
              onReorder={handleReorder}
              renderItem={(doc) => {
                const canPreview = isPreviewable(doc.contentType);
                const isLoadingPreview = previewLoading === doc.id;
                return (
                  <div className="doc-item">
                    <div
                      className={`doc-thumb-wrap${canPreview ? ' doc-thumb-wrap--previewable' : ''}`}
                      onClick={() => canPreview && handlePreview(doc)}
                      title={canPreview ? 'Click to preview' : undefined}
                    >
                      {isLoadingPreview && (
                        <div className="doc-thumb-loading">
                          <LoadingSpinner small />
                        </div>
                      )}
                      {thumbUrls[doc.id] ? (
                        <img
                          className="doc-thumb"
                          src={thumbUrls[doc.id]}
                          alt={doc.fileName}
                        />
                      ) : (
                        <div className="doc-thumb doc-thumb--placeholder">
                          {doc.contentType.startsWith('image/') ? (
                            <span className="doc-thumb-icon">&#128247;</span>
                          ) : doc.contentType === 'application/pdf' ? (
                            <span className="doc-thumb-icon">&#128196;</span>
                          ) : (
                            <span className="doc-thumb-icon">&#128196;</span>
                          )}
                        </div>
                      )}
                      {canPreview && !isLoadingPreview && (
                        <div className="doc-thumb-preview-hint">Preview</div>
                      )}
                    </div>
                    <div className="doc-item-info">
                      <div
                        className={`doc-item-name${canPreview ? ' doc-item-name--previewable' : ''}`}
                        onClick={() => canPreview && handlePreview(doc)}
                      >
                        {doc.fileName}
                      </div>
                      <div className="doc-item-meta">
                        <span>{categoryLabels[doc.category] || 'Other'}</span>
                        <span>{formatFileSize(doc.fileSizeBytes)}</span>
                      </div>
                    </div>
                    <div className="doc-item-actions">
                      <button onClick={() => handleDownload(doc)}>Download</button>
                      <button
                        className="doc-delete-btn"
                        onClick={() => setConfirmDelete(doc)}
                        disabled={!canDelete}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                );
              }}
            />
          </div>
        )}
      </DropZone>

      {previewDoc && (
        <div className="doc-preview-overlay" onClick={closePreview}>
          <div className="doc-preview-modal" onClick={e => e.stopPropagation()}>
            <div className="doc-preview-header">
              <span className="doc-preview-filename">{previewDoc.fileName}</span>
              <div className="doc-preview-header-actions">
                <button
                  className="doc-preview-download"
                  onClick={() => handleDownload(previewDoc)}
                  title="Download"
                >
                  &#8681; Download
                </button>
                <button
                  className="doc-preview-close"
                  onClick={closePreview}
                  aria-label="Close preview"
                >
                  &#10005;
                </button>
              </div>
            </div>
            <div className="doc-preview-body">
              {!previewUrl ? (
                <div className="doc-preview-spinner"><LoadingSpinner /></div>
              ) : previewDoc.contentType.startsWith('image/') ? (
                <img
                  className="doc-preview-img"
                  src={previewUrl}
                  alt={previewDoc.fileName}
                />
              ) : previewDoc.contentType === 'application/pdf' ? (
                <iframe
                  className="doc-preview-pdf"
                  src={previewUrl}
                  title={previewDoc.fileName}
                />
              ) : null}
            </div>
          </div>
        </div>
      )}

      {confirmDelete && (
        <div className="doc-confirm-overlay" onClick={() => !deleting && setConfirmDelete(null)}>
          <div className="doc-confirm-dialog" onClick={e => e.stopPropagation()}>
            <h4>Delete Document</h4>
            <p>
              Are you sure you want to delete <strong>{confirmDelete.fileName}</strong>?
              This action cannot be undone.
            </p>
            <div className="doc-confirm-actions">
              <button
                className="doc-confirm-delete"
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
              <button
                className="doc-confirm-cancel"
                onClick={() => setConfirmDelete(null)}
                disabled={deleting}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
